/**
 * The ambience bed of the war room, all procedural and all very quiet:
 *  - room tone (brown noise, low-passed) and wind (band-passed noise that slowly wanders and
 *    breathes) — darker and windier at night;
 *  - distant artillery: reports whose density follows the battles in `view.battles`, weighted by
 *    how close they are to the camera (see intensity.ts), panned to their side and louder at
 *    night. Silent while the game is paused or over;
 *  - a distant church bell at dawn (05:00), at most once in a while at high game speeds;
 *  - weather: rain hissing on the roof and the heavy roar of a storm, following the rain under
 *    the camera (weatherSound.ts); the wind rises in storms and blizzards; thunder rolls after
 *    each lightning flash the renderer draws, later and duller the farther away it struck.
 * Reduced motion (setting or OS) keeps the bed still and softens the guns' transients.
 */
import { hourOfDay, isNight } from '@friction/engine';
import { prefersReducedMotion } from '../app/settings';
import { bus } from '../game/bus';
import { gameStore } from '../game/store';
import { type CameraRect, gunfire, pickSource } from './intensity';
import { isAudible, type Mixer, onAudioReady } from './mixer';
import { env, tone } from './sfx';
import { thunder, type WeatherMix, weatherMix } from './weatherSound';

/** Scheduler period for the guns (s). */
const GUN_STEP = 0.2;
/** The dawn bell rings at most this often (ms of real time). */
const BELL_COOLDOWN_MS = 45_000;

interface Bed {
  roomFilter: BiquadFilterNode;
  windGain: GainNode;
  sweepDepth: GainNode;
  breathDepth: GainNode;
  /** Shared "open air" reverb send for guns and bells. */
  air: ConvolverNode;
  /** Rain hiss (band-limited white noise) and the low roar of heavy rain. */
  rainGain: GainNode;
  roarGain: GainNode;
}

/** Peak gains of the weather layers (the bed stays quiet: room tone is 0.05 of brown noise). */
const RAIN_GAIN = 0.013;
const ROAR_GAIN = 0.05;
/** Base wind gain by day / night, and how much a gale adds. */
const WIND_DAY = 0.01;
const WIND_NIGHT = 0.014;
const WIND_GALE = 0.022;

/** Decaying stereo noise: the impulse response of a wide, open landscape. */
function airImpulse(c: BaseAudioContext, seconds: number): AudioBuffer {
  const n = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, n, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n) ** 3;
  }
  return buf;
}

function startBed(m: Mixer): Bed {
  const c = m.ctx;
  const t = c.currentTime;

  const room = c.createBufferSource();
  room.buffer = m.brown;
  room.loop = true;
  const roomFilter = c.createBiquadFilter();
  roomFilter.type = 'lowpass';
  roomFilter.frequency.value = 260;
  const roomGain = c.createGain();
  roomGain.gain.setValueAtTime(0.0001, t);
  roomGain.gain.exponentialRampToValueAtTime(0.05, t + 4);
  room.connect(roomFilter).connect(roomGain).connect(m.amb);
  room.start();

  // A 7 s buffer (not a multiple of the 6 s room loop) so the two beds never phase-lock.
  const windBuf = c.createBuffer(1, c.sampleRate * 7, c.sampleRate);
  const wd = windBuf.getChannelData(0);
  for (let i = 0; i < wd.length; i++) wd[i] = Math.random() * 2 - 1;
  const wind = c.createBufferSource();
  wind.buffer = windBuf;
  wind.loop = true;
  const windFilter = c.createBiquadFilter();
  windFilter.type = 'bandpass';
  windFilter.frequency.value = 420;
  windFilter.Q.value = 0.9;
  const sweep = c.createOscillator();
  sweep.frequency.value = 0.031;
  const sweepDepth = c.createGain();
  sweepDepth.gain.value = 180;
  sweep.connect(sweepDepth).connect(windFilter.frequency);
  const windGain = c.createGain();
  windGain.gain.value = 0.01;
  const breath = c.createOscillator();
  breath.frequency.value = 0.017;
  const breathDepth = c.createGain();
  breathDepth.gain.value = 0.006;
  breath.connect(breathDepth).connect(windGain.gain);
  wind.connect(windFilter).connect(windGain).connect(m.amb);
  wind.start();
  sweep.start();
  breath.start();

  const air = c.createConvolver();
  air.buffer = airImpulse(c, 2.6);
  const airOut = c.createGain();
  airOut.gain.value = 0.5;
  air.connect(airOut).connect(m.amb);

  // Rain on the roof: 3 s of stereo white noise (never phase-locks with the other loops),
  // band-limited to a soft hiss; a storm adds the low roar of the brown-noise bed.
  const rainBuf = c.createBuffer(2, c.sampleRate * 3, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = rainBuf.getChannelData(ch);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  const rain = c.createBufferSource();
  rain.buffer = rainBuf;
  rain.loop = true;
  const rainHp = c.createBiquadFilter();
  rainHp.type = 'highpass';
  rainHp.frequency.value = 1100;
  const rainLp = c.createBiquadFilter();
  rainLp.type = 'lowpass';
  rainLp.frequency.value = 6200;
  const rainGain = c.createGain();
  rainGain.gain.value = 0;
  rain.connect(rainHp).connect(rainLp).connect(rainGain).connect(m.amb);
  rain.start();
  const roar = c.createBufferSource();
  roar.buffer = m.brown;
  roar.loop = true;
  roar.playbackRate.value = 1.7;
  const roarLp = c.createBiquadFilter();
  roarLp.type = 'lowpass';
  roarLp.frequency.value = 520;
  const roarGain = c.createGain();
  roarGain.gain.value = 0;
  roar.connect(roarLp).connect(roarGain).connect(m.amb);
  roar.start(0, 2.3);

  return { roomFilter, windGain, sweepDepth, breathDepth, air, rainGain, roarGain };
}

/** Night darkens the room and lets the wind up; reduced motion stills the wandering. */
function shapeBed(m: Mixer, bed: Bed, night: boolean, still: boolean, wx: WeatherMix): void {
  const t = m.ctx.currentTime;
  bed.roomFilter.frequency.setTargetAtTime(night ? 190 : 260, t, 8);
  bed.windGain.gain.setTargetAtTime((night ? WIND_NIGHT : WIND_DAY) + WIND_GALE * wx.wind, t, 4);
  bed.sweepDepth.gain.setTargetAtTime(still ? 20 : 180 + 260 * wx.wind, t, 2);
  bed.breathDepth.gain.setTargetAtTime(still ? 0.001 : 0.006 + 0.008 * wx.wind, t, 2);
  bed.rainGain.gain.setTargetAtTime(RAIN_GAIN * wx.rain, t, 2.5);
  bed.roarGain.gain.setTargetAtTime(ROAR_GAIN * wx.storm, t, 2.5);
}

/** Thunder: a crack when near, then a long, rolling, low-passed rumble. */
function thunderRoll(m: Mixer, bed: Bed, near: number, at: number): void {
  const c = m.ctx;
  const out = c.createBiquadFilter();
  out.type = 'lowpass';
  out.frequency.value = 90 + 260 * near;
  const g = c.createGain();
  g.gain.value = 1;
  out.connect(g);
  g.connect(m.amb);
  g.connect(bed.air);
  const loud = 0.1 + 0.22 * near;
  const len = 3 + 2.5 * (1 - near);
  // Two or three overlapping rolls of brown noise make the rumble uneven.
  for (let k = 0; k < 3; k++) {
    const src = c.createBufferSource();
    src.buffer = m.brown;
    src.playbackRate.value = 0.6 + 0.3 * Math.random();
    const start = at + k * (0.25 + Math.random() * 0.5);
    src.connect(env(c, out, loud * (1 - k * 0.25), 0.08 + 0.2 * (1 - near), len * (1 - k * 0.2), start));
    src.start(start, Math.random() * 4, len + 0.5);
  }
  if (near > 0.6) {
    const crack = c.createBufferSource();
    crack.buffer = m.white;
    const hp = c.createBiquadFilter();
    hp.type = 'bandpass';
    hp.frequency.value = 1400;
    hp.Q.value = 0.7;
    crack.connect(hp).connect(env(c, m.amb, 0.05 * near, 0.004, 0.35, at));
    crack.start(at);
  }
  window.setTimeout(() => g.disconnect(), (at - c.currentTime + len + 2) * 1000);
}

/** One distant report: a sub thump and a dull roll of noise, panned and sent into the air. */
function report(m: Mixer, bed: Bed, pan: number, near: number, loud: number, at: number): void {
  const c = m.ctx;
  const p = c.createStereoPanner();
  p.pan.value = pan;
  p.connect(m.amb);
  const send = c.createGain();
  send.gain.value = 0.6 - 0.3 * near;
  send.connect(bed.air);
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 70 + 150 * near;
  lp.Q.value = 0.6;
  lp.connect(p);
  lp.connect(send);
  const roll = c.createBufferSource();
  roll.buffer = m.brown;
  const decay = 0.8 + 1.4 * (1 - near) + Math.random() * 0.4;
  roll.connect(env(c, lp, loud, 0.015 + 0.03 * (1 - near), decay, at));
  roll.start(at, Math.random() * 5, decay + 0.1);
  tone(c, lp, 'sine', 50 + 16 * near, 30, loud * 0.9, 0.35 + 0.35 * near, at, 0.008);
  window.setTimeout(() => p.disconnect(), (at - c.currentTime + decay + 1) * 1000);
}

/** A church bell far away: a few inharmonic partials with long decays, struck three times. */
function dawnBell(m: Mixer, bed: Bed): void {
  const c = m.ctx;
  const out = c.createBiquadFilter();
  out.type = 'lowpass';
  out.frequency.value = 1700;
  const g = c.createGain();
  g.gain.value = 0.55;
  out.connect(g);
  g.connect(m.amb);
  g.connect(bed.air);
  const f0 = 233;
  const partials: [number, number, number][] = [
    [0.5, 0.35, 4.5],
    [1, 0.5, 3.4],
    [1.19, 0.3, 2.8],
    [1.5, 0.18, 2.4],
    [2, 0.32, 2.1],
    [2.52, 0.1, 1.4],
    [3.01, 0.07, 1.1],
  ];
  const t0 = c.currentTime + 0.05;
  [0, 2.4, 4.8].forEach((dt, strike) => {
    const level = 0.03 * (1 - strike * 0.15);
    for (const [ratio, amp, decay] of partials)
      tone(c, out, 'sine', f0 * ratio, f0 * ratio, level * amp, decay, t0 + dt, 0.004);
  });
  window.setTimeout(() => g.disconnect(), 12_000);
}

/** Start the ambience; it waits for the first user gesture (see mixer.unlockAudio). */
export function startAmbience(): () => void {
  let camera: CameraRect | null = null;
  let bed: Bed | null = null;
  let mixer: Mixer | null = null;
  let lastBell = -BELL_COOLDOWN_MS;
  let shaped = '';
  let night = true;
  const offs: (() => void)[] = [];
  /** Re-shape the bed only when night, the motion preference or the weather audibly changes. */
  const reshape = () => {
    const still = prefersReducedMotion();
    const g = gameStore.getState();
    const wx = weatherMix(g.view?.weather ?? null, g.map, g.grid, camera);
    const q = (x: number) => Math.round(x * 20);
    const key = `${night}:${still}:${q(wx.rain)}:${q(wx.storm)}:${q(wx.snow)}:${q(wx.wind)}`;
    if (!mixer || !bed || key === shaped) return;
    shaped = key;
    shapeBed(mixer, bed, night, still, wx);
  };

  offs.push(bus.on('camera', (c) => (camera = c)));
  offs.push(
    onAudioReady((m) => {
      mixer = m;
      bed = startBed(m);
      const v = gameStore.getState().view;
      night = v ? isNight(v.tick) : true;
      reshape();
    }),
  );

  // Night / dawn follow the in-game clock.
  offs.push(
    gameStore.subscribe((st, prev) => {
      if (!mixer || !bed || !st.view) return;
      const tick = st.view.tick;
      const before = prev.view?.tick;
      if (tick === before) return;
      night = isNight(tick);
      reshape();
      const now = performance.now();
      if (
        before !== undefined &&
        tick - before <= 3 &&
        hourOfDay(tick) === 5 &&
        hourOfDay(before) !== 5 &&
        now - lastBell > BELL_COOLDOWN_MS &&
        isAudible()
      ) {
        lastBell = now;
        dawnBell(mixer, bed);
      }
    }),
  );

  // Thunder answers the renderer's lightning.
  offs.push(
    bus.on('lightning', ({ x, y }) => {
      if (!mixer || !bed || !isAudible()) return;
      const { delay, near } = thunder(x, y, camera);
      thunderRoll(mixer, bed, near, mixer.ctx.currentTime + delay);
    }),
  );

  // The weather under the camera changes as the camera pans and the systems drift.
  const weatherTimer = window.setInterval(reshape, 500);

  // Guns: a Poisson process re-evaluated every GUN_STEP seconds from the current battles.
  const timer = window.setInterval(() => {
    if (!mixer || !bed || !isAudible()) return;
    const g = gameStore.getState();
    if (!g.view || !g.grid || g.paused || g.view.winners) return;
    const grid = g.grid;
    const night = isNight(g.view.tick);
    const fire = gunfire(g.view.battles, (h) => [grid.cx(h), grid.cy(h)], camera, night);
    if (fire.rate <= 0 || Math.random() > 1 - Math.exp(-fire.rate * GUN_STEP)) return;
    const src = pickSource(fire.sources, Math.random());
    if (!src) return;
    const soft = prefersReducedMotion();
    const loud = (night ? 0.2 : 0.15) * (0.4 + 0.6 * src.near) * (soft ? 0.6 : 1);
    const at = mixer.ctx.currentTime + Math.random() * GUN_STEP;
    report(mixer, bed, src.pan, src.near, loud, at);
    // Batteries often fire in salvos: a couple of follow-up reports close behind.
    if (!soft && Math.random() < 0.3)
      for (let i = 1; i <= 1 + Math.floor(Math.random() * 2); i++)
        report(
          mixer,
          bed,
          src.pan + (Math.random() - 0.5) * 0.2,
          src.near,
          loud * 0.7,
          at + i * (0.2 + Math.random() * 0.5),
        );
  }, GUN_STEP * 1000);

  return () => {
    window.clearInterval(timer);
    window.clearInterval(weatherTimer);
    for (const off of offs) off();
  };
}
